import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { BlocksRenderer } from "../components/BlocksRenderer";
import { usePersonas } from "../hooks/useCms";
import { useSiteConfig } from "../hooks/useSiteConfig";
import { mediaUrl } from "../lib/strapi";
import type { Persona } from "../types/cms";

/**
 * Página de equipo estable: /proyecto/equipo
 *
 * Lista las Personas del CMS con `equipoEstable = true`. La idea es
 * mostrar a la dirección, coordinación y al núcleo docente que se
 * mantiene de un año al siguiente. Los monitores puntuales de una
 * edición concreta NO entran aquí — aparecen en la página de detalle
 * de su edición.
 *
 * RGPD:
 *   - Foto SÓLO si `consentimientoRgpd = true`. El backend ya filtra
 *     pero el frontend lo vuelve a comprobar como defensa en
 *     profundidad.
 *   - Si no hay foto consentida, mostramos un avatar con las iniciales
 *     sobre fondo de marca; nunca un placeholder anónimo que parezca
 *     una foto real.
 */

// Etiquetas humanas para las disciplinas del enum del CMS.
const disciplinaLabel: Record<NonNullable<Persona["disciplinas"]>, string> = {
  programacion: "Programación",
  robotica: "Robótica",
  quimica: "Química",
  fisica: "Física",
  biologia: "Biología",
  matematicas: "Matemáticas",
  arte: "Arte",
  historia: "Historia",
  musica: "Música",
  arquitectura: "Arquitectura",
};

export function EquipoPage() {
  const { data: personas, isLoading } = usePersonas(true);
  const site = useSiteConfig();

  return (
    <>
      <Helmet>
        <title>Equipo — Campus EMJC</title>
        <meta
          name="description"
          content="Profesoras, investigadores y coordinadores del equipo estable de la Escuela Municipal de Jóvenes Científicos de Extremadura."
        />
      </Helmet>

      <section className="container-emjc py-20">
        <nav aria-label="Migas de pan" className="text-sm text-gray-700">
          <Link to="/proyecto" className="hover:text-brand-orange-deep hover:underline">
            El proyecto
          </Link>
          <span aria-hidden="true" className="mx-2">/</span>
          <span aria-current="page">Equipo</span>
        </nav>

        <header className="mt-6">
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange-deep">
            Quiénes somos
          </p>
          <h1 className="mt-2 max-w-3xl font-display text-5xl font-semibold leading-tight md:text-6xl">
            El equipo que sostiene la EMJC.
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-gray-700">
            Profesoras, investigadores y coordinadores de la Universidad de
            Extremadura que dirigen la escuela todo el año. Cada edición se
            apoya además en monitores puntuales — los encontrarás en la
            página de cada edición.
          </p>
        </header>

        {isLoading && (
          <p className="mt-12 text-gray-700">Cargando equipo…</p>
        )}

        {!isLoading && (!personas || personas.length === 0) && (
          <div className="mt-12 rounded-2xl border border-gray-300 bg-white p-8">
            <p className="font-display text-xl font-semibold">
              Estamos preparando esta página.
            </p>
            <p className="mt-3 text-gray-700">
              El equipo se está dando de alta en el panel y aparecerá aquí en
              cuanto esté publicado. Mientras tanto, puedes escribirnos a{" "}
              <a
                href={`mailto:${site.email}`}
                className="font-semibold text-brand-orange-deep underline underline-offset-4"
              >
                {site.email}
              </a>
              .
            </p>
          </div>
        )}

        {personas && personas.length > 0 && (
          <ul className="mt-12 grid gap-8 md:grid-cols-2 xl:grid-cols-3">
            {personas.map((p) => (
              <li key={p.documentId ?? p.id}>
                <PersonaArticulo persona={p} />
              </li>
            ))}
          </ul>
        )}

        <p className="mt-16 max-w-3xl text-sm text-gray-700">
          Las fotografías del equipo se publican únicamente con consentimiento
          firmado de cada persona. Si una ficha no muestra fotografía es porque
          la persona ha decidido no incluirla; no afecta a su pertenencia al
          equipo.
        </p>
      </section>
    </>
  );
}

function PersonaArticulo({ persona }: { persona: Persona }) {
  // RGPD: solo mostramos foto si la persona ha consentido.
  const fotoUrl =
    persona.consentimientoRgpd && persona.foto?.url
      ? mediaUrl(persona.foto.url)
      : undefined;

  const iniciales = persona.nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

  const disciplinaTxt = persona.disciplinas
    ? disciplinaLabel[persona.disciplinas]
    : null;

  return (
    <article className="flex h-full flex-col rounded-2xl border border-gray-300 bg-white p-6 transition-colors hover:border-brand-orange">
      <div className="flex items-center gap-4">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-orange-soft ring-1 ring-gray-300">
          {fotoUrl ? (
            <img
              src={fotoUrl}
              alt={persona.foto?.alternativeText ?? persona.nombre}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <span
              aria-hidden="true"
              className="font-display text-xl font-semibold text-brand-orange-deep"
            >
              {iniciales}
            </span>
          )}
        </div>
        <div className="min-w-0">
          <h2 className="truncate font-display text-lg font-semibold">
            {persona.nombre}
          </h2>
          {persona.rol && (
            <p className="truncate text-sm text-gray-700">{persona.rol}</p>
          )}
        </div>
      </div>

      {disciplinaTxt && (
        <p className="mt-4 inline-flex w-fit rounded-full bg-brand-paper px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-ink ring-1 ring-gray-300">
          {disciplinaTxt}
        </p>
      )}

      {persona.bio && persona.bio.length > 0 && (
        <div className="mt-4">
          <BlocksRenderer
            blocks={persona.bio}
            className="prose-emjc max-w-prose space-y-3 text-sm leading-relaxed text-gray-700"
          />
        </div>
      )}

      {(persona.linkedinUrl || persona.webPersonal) && (
        <ul className="mt-auto flex flex-wrap gap-3 pt-4 text-sm">
          {persona.linkedinUrl && (
            <li>
              <a
                href={persona.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-brand-orange-deep underline underline-offset-4 hover:text-brand-orange"
              >
                LinkedIn
              </a>
            </li>
          )}
          {persona.webPersonal && (
            <li>
              <a
                href={persona.webPersonal}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-brand-orange-deep underline underline-offset-4 hover:text-brand-orange"
              >
                Web personal
              </a>
            </li>
          )}
        </ul>
      )}
    </article>
  );
}
